import React, { useCallback, useContext, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  LinearProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowDownwardRoundedIcon from "@mui/icons-material/ArrowDownwardRounded";
import ArrowUpwardRoundedIcon from "@mui/icons-material/ArrowUpwardRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ForumRoundedIcon from "@mui/icons-material/ForumRounded";
import GroupsRoundedIcon from "@mui/icons-material/GroupsRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip as ChartTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Navigate } from "react-router-dom";
import { UserContext } from "../../context/UserProvider";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import { getErrorResponse, getHeader } from "../../utils/util";
import BackBtn from "../../widgets/BackBtn";
import MuPb from "../../widgets/MuPb";

const numberFormatter = new Intl.NumberFormat("en-IN");
const compactFormatter = new Intl.NumberFormat("en-IN", {
  notation: "compact",
  maximumFractionDigits: 1,
});

const formatNumber = (value) => numberFormatter.format(value ?? 0);
const formatCompact = (value) => compactFormatter.format(value ?? 0);
const responseCoverage = (incoming, outgoing) =>
  outgoing > 0 ? Math.round((incoming / outgoing) * 100) : 0;

const formatDate = (value) => {
  if (!value) return "No activity date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No activity date";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMonthYear = (year, month) =>
  new Date(year, month - 1, 1).toLocaleDateString("en-IN", {
    month: "short",
    year: "numeric",
  });

const SummaryCard = ({ label, value, helper, color, icon, prominent }) => (
  <Card
    variant="outlined"
    sx={{
      flex: prominent ? "2 1 300px" : "1 1 190px",
      minHeight: prominent ? 154 : 132,
      borderColor: `${color}33`,
      background: prominent
        ? `linear-gradient(135deg, ${color}18 0%, #ffffff 72%)`
        : "#fff",
      boxShadow: prominent ? `0 16px 36px ${color}18` : "none",
    }}
  >
    <CardContent sx={{ height: "100%" }}>
      <Box display="flex" alignItems="flex-start" justifyContent="space-between">
        <Box>
          <Typography variant="body2" color="text.secondary" fontWeight={600}>
            {label}
          </Typography>
          <Typography
            variant={prominent ? "h3" : "h4"}
            fontWeight={800}
            sx={{ color, mt: 0.8, letterSpacing: "-0.04em" }}
          >
            {formatCompact(value)}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: 2.5,
            display: "grid",
            placeItems: "center",
            color,
            bgcolor: `${color}14`,
          }}
        >
          {icon}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {helper}
      </Typography>
    </CardContent>
  </Card>
);

const MetricTile = ({ label, value, helper, color = "#334155", prominent }) => (
  <Box
    sx={{
      p: prominent ? 2.5 : 2,
      borderRadius: 3,
      border: `1px solid ${color}2b`,
      bgcolor: prominent ? `${color}0d` : "background.paper",
      minHeight: 116,
    }}
  >
    <Typography variant="caption" color="text.secondary" fontWeight={700}>
      {label}
    </Typography>
    <Typography
      variant={prominent ? "h3" : "h4"}
      fontWeight={800}
      sx={{ color, my: 0.5, letterSpacing: "-0.04em" }}
    >
      {formatNumber(value)}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {helper}
    </Typography>
  </Box>
);

const EmptyState = () => (
  <Paper
    variant="outlined"
    sx={{ py: 8, px: 3, textAlign: "center", borderStyle: "dashed" }}
  >
    <ForumRoundedIcon sx={{ fontSize: 42, color: "text.disabled", mb: 1 }} />
    <Typography variant="h6" fontWeight={700}>
      No chat activity found
    </Typography>
    <Typography color="text.secondary" variant="body2">
      Non-template WhatsApp conversations will appear here once messages are
      exchanged.
    </Typography>
  </Paper>
);

const ChatStatsDetailDialog = ({ customerId, open, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const loadDetail = useCallback(async () => {
    if (!customerId) return;
    setLoading(true);
    setError("");
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/user/get-account-chat-stats?customerId=${encodeURIComponent(
          customerId
        )}`,
        getHeader()
      );
      setDetail(response.data.data);
    } catch (requestError) {
      setError(getErrorResponse(requestError));
    } finally {
      setLoading(false);
    }
  }, [customerId]);

  React.useEffect(() => {
    if (open) loadDetail();
  }, [open, loadDetail]);

  const summary = detail?.summary ?? {};
  const coverage = responseCoverage(
    summary.uniqueEnquirers,
    summary.uniqueEngaged
  );
  const monthlyOutgoing = (detail?.monthlyOutgoing ?? []).map((item) => ({
    ...item,
    label: formatMonthYear(item.year, item.month),
  }));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="flex-start" justifyContent="space-between">
          <Box minWidth={0}>
            <Typography variant="overline" color="success.main" fontWeight={800}>
              WhatsApp conversation details
            </Typography>
            <Typography variant="h5" fontWeight={800} noWrap>
              {customerId}
            </Typography>
          </Box>
          <IconButton onClick={onClose} aria-label="Close details">
            <CloseRoundedIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ bgcolor: "#f8fafc" }}>
        {loading ? (
          <Box minHeight={420} display="grid" sx={{ placeItems: "center" }}>
            <CircularProgress color="success" />
          </Box>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={loadDetail}>
                Retry
              </Button>
            }
          >
            {error}
          </Alert>
        ) : detail ? (
          <Box display="flex" flexDirection="column" gap={2.5}>
            <Box display="flex" gap={1} flexWrap="wrap">
              {(detail.phoneNumberIds ?? []).map((phoneNumberId) => (
                <Chip
                  key={phoneNumberId}
                  label={`Phone ID ${phoneNumberId}`}
                  size="small"
                  variant="outlined"
                />
              ))}
              <Chip
                label={`Last active: ${formatDate(summary.lastActivity)}`}
                size="small"
                sx={{ bgcolor: "#fff" }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "1.35fr repeat(3, 1fr)",
                },
                gap: 1.5,
              }}
            >
              <MetricTile
                label="Incoming messages"
                value={summary.incomingMessages}
                helper="Replies and enquiries received"
                color="#16a34a"
                prominent
              />
              <MetricTile
                label="Outgoing messages"
                value={summary.outgoingMessages}
                helper="Non-template messages sent"
                color="#2563eb"
              />
              <MetricTile
                label="Unique enquirers"
                value={summary.uniqueEnquirers}
                helper="Distinct people who wrote in"
                color="#7c3aed"
              />
              <MetricTile
                label="Unique engaged"
                value={summary.uniqueEngaged}
                helper="Distinct outgoing recipients"
                color="#ea580c"
              />
            </Box>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Box
                display="flex"
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={1}
              >
                <Box>
                  <Typography fontWeight={800}>Response coverage</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Unique enquirers compared with unique outgoing contacts
                  </Typography>
                </Box>
                <Typography variant="h4" color="success.main" fontWeight={800}>
                  {coverage}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(coverage, 100)}
                color="success"
                sx={{ height: 8, borderRadius: 5, mt: 1.5 }}
              />
            </Paper>

            <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
              <Box
                display="flex"
                alignItems={{ xs: "flex-start", sm: "center" }}
                justifyContent="space-between"
                flexDirection={{ xs: "column", sm: "row" }}
                gap={1}
                mb={2}
              >
                <Box>
                  <Typography fontWeight={800}>Monthly outgoing volume</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Non-template messages sent each calendar month
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={`${monthlyOutgoing.length} active month${
                    monthlyOutgoing.length === 1 ? "" : "s"
                  }`}
                />
              </Box>
              {monthlyOutgoing.length > 0 ? (
                <Box height={320}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={monthlyOutgoing}
                      margin={{ top: 8, right: 12, left: 0, bottom: 16 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11 }}
                        minTickGap={28}
                        angle={monthlyOutgoing.length > 12 ? -30 : 0}
                        textAnchor={monthlyOutgoing.length > 12 ? "end" : "middle"}
                        height={monthlyOutgoing.length > 12 ? 58 : 30}
                      />
                      <YAxis
                        allowDecimals={false}
                        tick={{ fontSize: 11 }}
                        tickFormatter={formatCompact}
                      />
                      <ChartTooltip
                        labelFormatter={(_, payload) =>
                          payload?.[0]?.payload?.label ?? ""
                        }
                        formatter={(value) => [
                          formatNumber(value),
                          "Outgoing messages",
                        ]}
                      />
                      <Bar
                        dataKey="outgoingMessages"
                        name="Outgoing messages"
                        fill="#2563eb"
                        radius={[6, 6, 0, 0]}
                        maxBarSize={46}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              ) : (
                <Box height={220} display="grid" sx={{ placeItems: "center" }}>
                  <Typography color="text.secondary">
                    No dated outgoing activity is available
                  </Typography>
                </Box>
              )}
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.45fr 1fr" },
                gap: 2,
              }}
            >
              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography fontWeight={800}>Current calendar month</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Daily incoming and outgoing messages for the current month
                </Typography>
                {(detail.dailyActivity ?? []).length > 0 ? (
                  <Box height={300}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={detail.dailyActivity}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={22} />
                        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                        <ChartTooltip />
                        <Legend />
                        <Bar
                          dataKey="incomingMessages"
                          name="Incoming"
                          fill="#16a34a"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="outgoingMessages"
                          name="Outgoing"
                          fill="#2563eb"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box height={300} display="grid" sx={{ placeItems: "center" }}>
                    <Typography color="text.secondary">
                      No conversation activity in the current month
                    </Typography>
                  </Box>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 3 }}>
                <Typography fontWeight={800}>Message mix</Typography>
                <Typography variant="body2" color="text.secondary" mb={2}>
                  Non-template messages by format
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {(detail.messageTypes ?? []).map((item) => (
                    <Chip
                      key={item.type}
                      label={`${item.type}: ${formatNumber(item.total)}`}
                      variant="outlined"
                      sx={{ textTransform: "capitalize" }}
                    />
                  ))}
                  {(detail.messageTypes ?? []).length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No message types available
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Box>

            <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
              <Box sx={{ px: 2.5, py: 2 }}>
                <Typography fontWeight={800}>Top conversations</Typography>
                <Typography variant="body2" color="text.secondary">
                  External numbers with the highest two-way activity
                </Typography>
              </Box>
              <Divider />
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Contact</TableCell>
                      <TableCell align="right">Incoming</TableCell>
                      <TableCell align="right">Outgoing</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell>Last activity</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(detail.topContacts ?? []).map((contact) => (
                      <TableRow key={contact.phoneNumber} hover>
                        <TableCell>
                          <Typography variant="body2" fontWeight={700}>
                            {contact.profileName === "undefined"
                              ? "~"
                              : contact.profileName || "Unknown contact"}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {contact.phoneNumber}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ color: "success.main", fontWeight: 700 }}>
                          {formatNumber(contact.incomingMessages)}
                        </TableCell>
                        <TableCell align="right">
                          {formatNumber(contact.outgoingMessages)}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700 }}>
                          {formatNumber(contact.totalMessages)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="caption">
                            {formatDate(contact.lastActivity)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                    {(detail.topContacts ?? []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          No contact activity available
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

const WhatsAppChatStats = () => {
  const { user } = useContext(UserContext);
  const isAllowed = ["Root", "SuperAdmin"].includes(user?.userType);
  const { data, isLoading, isFetching, isError, error, refetch } = useApiEp(
    "/user/get-account-chat-stats",
    isAllowed
  );
  const [search, setSearch] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return data?.data ?? [];
    return (data?.data ?? []).filter((row) =>
      row.customerId?.toLowerCase().includes(query)
    );
  }, [data, search]);

  if (!isAllowed) return <Navigate to="/home/dashboard" replace />;
  if (isLoading) return <MuPb />;

  return (
    <Container maxWidth="xl" sx={{ py: 3.5 }}>
      <BackBtn />
      <Box
        display="flex"
        alignItems={{ xs: "flex-start", md: "center" }}
        justifyContent="space-between"
        flexDirection={{ xs: "column", md: "row" }}
        gap={2}
        mt={2}
        mb={3}
      >
        <Box>
          <Typography variant="overline" color="success.main" fontWeight={800}>
            WhatsApp Campaign
          </Typography>
          <Typography variant="h4" fontWeight={850} letterSpacing="-0.035em">
            Conversation analytics
          </Typography>
          <Typography color="text.secondary" mt={0.5}>
            See which customer accounts start the most chats—and which ones get
            people talking back.
          </Typography>
        </Box>
        <Tooltip title="Refresh stats">
          <span>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={
                isFetching ? <CircularProgress size={17} /> : <RefreshRoundedIcon />
              }
              disabled={isFetching}
              onClick={() => refetch()}
            >
              Refresh
            </Button>
          </span>
        </Tooltip>
      </Box>

      {isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {getErrorResponse(error)}
        </Alert>
      ) : (
        <>
          <Box display="flex" gap={1.5} flexWrap="wrap" mb={3}>
            <SummaryCard
              label="Incoming messages"
              value={data?.summary?.incomingMessages}
              helper="Customer replies and enquiries received"
              color="#16a34a"
              icon={<ArrowDownwardRoundedIcon />}
              prominent
            />
            <SummaryCard
              label="Outgoing chats"
              value={data?.summary?.outgoingMessages}
              helper="Non-template messages sent"
              color="#2563eb"
              icon={<ArrowUpwardRoundedIcon />}
            />
            <SummaryCard
              label="Unique enquirers"
              value={data?.summary?.uniqueEnquirers}
              helper="Distinct numbers that messaged in"
              color="#7c3aed"
              icon={<ForumRoundedIcon />}
            />
            <SummaryCard
              label="Unique engaged"
              value={data?.summary?.uniqueEngaged}
              helper="Distinct numbers reached in chats"
              color="#ea580c"
              icon={<GroupsRoundedIcon />}
            />
          </Box>

          <Paper variant="outlined" sx={{ borderRadius: 4, overflow: "hidden" }}>
            <Box
              sx={{
                p: 2.5,
                display: "flex",
                alignItems: { xs: "stretch", sm: "center" },
                justifyContent: "space-between",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight={800}>
                  Customer leaderboard
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Ranked by outgoing non-template chat messages
                </Typography>
              </Box>
              <TextField
                size="small"
                placeholder="Search customer ID"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                sx={{ minWidth: { sm: 280 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Divider />

            {(data?.data ?? []).length === 0 ? (
              <Box p={2.5}>
                <EmptyState />
              </Box>
            ) : (
              <TableContainer>
                <Table sx={{ minWidth: 980 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell width={72}>Rank</TableCell>
                      <TableCell>Customer ID</TableCell>
                      <TableCell>Outgoing messages</TableCell>
                      <TableCell align="right">Incoming messages</TableCell>
                      <TableCell align="right">Unique enquirers</TableCell>
                      <TableCell align="right">Unique engaged</TableCell>
                      <TableCell align="right">Response coverage</TableCell>
                      <TableCell align="right">Details</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row) => {
                      const coverage = responseCoverage(
                        row.uniqueEnquirers,
                        row.uniqueEngaged
                      );
                      const maxOutgoing = data?.data?.[0]?.outgoingMessages || 1;
                      const progress = Math.round(
                        (row.outgoingMessages / maxOutgoing) * 100
                      );
                      const originalRank = (data?.data ?? []).findIndex(
                        (item) => item.customerId === row.customerId
                      );

                      return (
                        <TableRow key={row.customerId} hover>
                          <TableCell>
                            <Box
                              sx={{
                                width: 34,
                                height: 34,
                                borderRadius: "50%",
                                display: "grid",
                                placeItems: "center",
                                fontWeight: 800,
                                bgcolor: originalRank < 3 ? "#fef3c7" : "action.hover",
                                color: originalRank < 3 ? "#92400e" : "text.secondary",
                              }}
                            >
                              {originalRank + 1}
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography fontWeight={800}>{row.customerId}</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Last active {formatDate(row.lastActivity)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box minWidth={150}>
                              <Box display="flex" justifyContent="space-between" mb={0.6}>
                                <Typography variant="body2" fontWeight={700}>
                                  {formatNumber(row.outgoingMessages)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {progress}% of leader
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={progress}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              icon={<ArrowDownwardRoundedIcon />}
                              label={formatNumber(row.incomingMessages)}
                              color="success"
                              sx={{ fontWeight: 800 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.uniqueEnquirers)}
                          </TableCell>
                          <TableCell align="right">
                            {formatNumber(row.uniqueEngaged)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              fontWeight={800}
                              color={coverage > 0 ? "success.main" : "text.secondary"}
                            >
                              {coverage}%
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              endIcon={<OpenInNewRoundedIcon />}
                              onClick={() => setSelectedCustomerId(row.customerId)}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {rows.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} align="center" sx={{ py: 7 }}>
                          No customer ID matches “{search}”.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        </>
      )}

      <ChatStatsDetailDialog
        customerId={selectedCustomerId}
        open={Boolean(selectedCustomerId)}
        onClose={() => setSelectedCustomerId("")}
      />
    </Container>
  );
};

export default WhatsAppChatStats;
